/**
 * The suggested questions — `03 §8 WP-M4`'s *"khu hỏi đáp có câu hỏi gợi ý"*.
 *
 * Pure: no React. A catalogue rather than JSX in the panel, so a test can assert
 * things about the whole set rather than about whichever button it happened to click.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  A SUGGESTION IS A PRODUCT DECISION ABOUT WHAT PEOPLE WILL ASK
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * A prefilled question is not a convenience; it is the shape of the question most
 * people will end up asking, because most people press a button rather than write a
 * sentence. Master §18.3 forbids the AI from concluding fraud, from recommending
 * discipline, dismissal or pay deduction, and from reading private content — and
 * `refused: true` exists on `AiChatResponse` precisely because the model is expected
 * to decline questions of that shape.
 *
 * So every suggestion below asks about **work**, over a period, at the aggregate the
 * retrieval layer actually reads. None names a person, because the retrieval layer
 * never sees a name — `wp_employees` is read for ids only, and
 * `AI_FORBIDDEN_COLUMNS` blocks `full_name` and `email` by name. A suggestion that
 * asked "is X slacking" would be a button whose best possible outcome is a refusal
 * and whose worst is an answer.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  PRESSING A SUGGESTION SENDS NOTHING
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * It fills the box. The person still presses *Hỏi*. Two reasons, and the second is
 * the one that matters: a chat turn is the **only** synchronous model call on this
 * build and it takes one of two per-process slots for up to sixty seconds, so a
 * suggestion that fired on click would let a stray tap consume half the company's
 * concurrency; and a question somebody has not read is a question they cannot own
 * when the answer is a sentence about a colleague.
 */

import type { MessageKey } from '../i18n/messages';

export interface SuggestedQuestion {
  /** Stable id; also the test id suffix, so a reader can cite one. */
  readonly id: string;
  /** The button's short label. */
  readonly labelKey: MessageKey;
  /** What is put in the box — the whole question, not a fragment. */
  readonly questionKey: MessageKey;
}

export const SUGGESTED_QUESTIONS: readonly SuggestedQuestion[] = [
  {
    id: 'hours-trend',
    labelKey: 'm4.ask.suggest.hours.label',
    questionKey: 'm4.ask.suggest.hours.question',
  },
  {
    id: 'workload-balance',
    labelKey: 'm4.ask.suggest.workload.label',
    questionKey: 'm4.ask.suggest.workload.question',
  },
  {
    id: 'completeness',
    labelKey: 'm4.ask.suggest.completeness.label',
    questionKey: 'm4.ask.suggest.completeness.question',
  },
  {
    id: 'alert-pattern',
    labelKey: 'm4.ask.suggest.alerts.label',
    questionKey: 'm4.ask.suggest.alerts.question',
  },
];

/** `AiChatRequest.message` — `minLength: 1`, `maxLength: 4000`. */
export const MAX_QUESTION_LENGTH = 4000;

/**
 * Is this draft one the client may send?
 *
 * Only the two bounds the contract publishes, and nothing else. The temptation is to
 * add a client-side rule about "questions the AI will refuse", and it is the wrong
 * instinct: the refusal is the **model's** judgement, it is published as
 * `refused: true` with the model's own sentence, and a client that pre-empted it
 * would be inventing a policy nobody could read, audit or appeal. What this client
 * refuses is only what the endpoint would reject with a `400` naming a field the
 * person never typed.
 */
export function isSendableQuestion(draft: string): boolean {
  const trimmed = draft.trim();
  return trimmed.length >= 1 && trimmed.length <= MAX_QUESTION_LENGTH;
}
